import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Plug, Globe, Code2, Trash2, Plus, CheckCircle2, XCircle,
  Loader2, Search, Server, Eye, EyeOff, Users, AlertCircle,
  Download, Upload, Sparkles, FileJson, ClipboardPaste, LayoutTemplate,
  Wand2, Copy, Heart, Stethoscope, Building2, Star,
} from 'lucide-react';

const FOREST = '#2C4A3B';
const TERRA = '#C47A5E';
const CHARCOAL = '#1C1917';

const SELECTOR_FIELDS = [
  { key: 'subjective', label: 'Subjective field', placeholder: '[data-field="subjective"] textarea' },
  { key: 'objective', label: 'Objective field', placeholder: '[aria-label="Objective"]' },
  { key: 'assessment', label: 'Assessment field', placeholder: '[data-section="assessment"] textarea' },
  { key: 'plan', label: 'Plan field', placeholder: '[data-section="plan"] textarea' },
  { key: 'transcript', label: 'Transcript field (optional)', placeholder: '[data-section="transcript"]' },
  { key: 'note_field', label: 'Fallback single note textarea', placeholder: 'textarea.note-body' },
  { key: 'patient_name', label: 'Patient name (read-only display)', placeholder: '.patient-header .name' },
  { key: 'chart_id', label: 'Chart ID (read-only display)', placeholder: '.chart-id' },
];

const VENDORS = [
  { value: 'epic', label: 'Epic' },
  { value: 'cerner', label: 'Oracle Cerner' },
  { value: 'athena', label: 'athenahealth' },
  { value: 'meditech', label: 'MEDITECH' },
  { value: 'allscripts', label: 'Allscripts' },
  { value: 'nextgen', label: 'NextGen' },
  { value: 'custom', label: 'Other / Custom FHIR server' },
];

export default function IntegrationsPage({ token }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('selectors');
  const [loading, setLoading] = useState(true);
  const [gateError, setGateError] = useState('');

  // Custom selectors
  const [customEhrs, setCustomEhrs] = useState([]);
  const [editingSelector, setEditingSelector] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  // FHIR
  const [fhirConfigs, setFhirConfigs] = useState([]);
  const [editingFhir, setEditingFhir] = useState(null);
  const [showSecret, setShowSecret] = useState(false);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    setGateError('');
    try {
      const [sel, fhir] = await Promise.all([
        axios.get(`${API}/integrations/custom-ehrs`, authHeader),
        axios.get(`${API}/integrations/fhir`, authHeader),
      ]);
      setCustomEhrs(sel.data.custom_ehrs || []);
      setFhirConfigs(fhir.data.fhir_configs || []);
    } catch (err) {
      const d = err.response?.data?.detail || 'Failed to load integrations';
      setGateError(d);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // ── Gate rendering ──────────────────────────────────────────
  if (!loading && gateError) {
    const isGated = /Enterprise|practice|upgrade/i.test(gateError);
    return (
      <div className="min-h-screen" style={{ background: '#F9F6F0', fontFamily: "'Manrope', sans-serif" }}>
        <div className="max-w-2xl mx-auto px-6 py-20">
          <div className="bg-white rounded-3xl border border-stone-200 p-10 text-center shadow-sm" data-testid="integrations-gate">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ background: FOREST }}>
              {isGated ? <Plug className="w-6 h-6 text-white" /> : <AlertCircle className="w-6 h-6 text-white" />}
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: CHARCOAL }}>
              {isGated ? 'Custom integrations are an Enterprise feature' : 'Integrations unavailable'}
            </h1>
            <p className="text-sm text-stone-500 mb-6">{gateError}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {isGated && (
                <Button onClick={() => navigate('/contact')} className="h-11 rounded-xl text-white font-semibold" style={{ background: TERRA }} data-testid="integrations-talk-to-sales">
                  Talk to Sales
                </Button>
              )}
              {/Create a practice/i.test(gateError) && (
                <Button onClick={() => navigate('/practice')} className="h-11 rounded-xl text-white font-semibold" style={{ background: FOREST }}>
                  Go to Practice
                </Button>
              )}
              <Button onClick={() => navigate('/')} variant="outline" className="h-11 rounded-xl">
                Back to dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── handlers ────────────────────────────────────────────────
  const saveSelector = async () => {
    const e = editingSelector;
    if (!e?.name?.trim() || !e?.url_pattern?.trim()) {
      toast.error('Name and URL pattern are required');
      return;
    }
    const payload = {
      name: e.name,
      url_pattern: e.url_pattern,
      selectors: e.selectors || {},
      enabled: e.enabled !== false,
      note_page_hints: e.note_page_hints || [],
      note_format: e.note_format || null,
      note_format_title: e.note_format_title || null,
    };
    try {
      if (e.id) {
        await axios.patch(`${API}/integrations/custom-ehrs/${e.id}`, payload, authHeader);
        toast.success('Custom EHR updated');
      } else {
        await axios.post(`${API}/integrations/custom-ehrs`, payload, authHeader);
        toast.success('Custom EHR added');
      }
      setEditingSelector(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    }
  };

  const deleteSelector = async (id) => {
    if (!window.confirm('Delete this custom EHR? Your clinicians will lose inject support for its pages.')) return;
    try {
      await axios.delete(`${API}/integrations/custom-ehrs/${id}`, authHeader);
      toast.success('Deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    }
  };

  const exportSelector = async (c) => {
    try {
      const { data } = await axios.get(`${API}/integrations/custom-ehrs/${c.id}/export`, authHeader);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plexura-ehr-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
  };

  const importTemplate = async (tplObject, overrideName) => {
    try {
      const { data } = await axios.post(
        `${API}/integrations/custom-ehrs/import`,
        { template: tplObject, override_name: overrideName || undefined },
        authHeader,
      );
      toast.success(`Imported "${data.name}" — click Edit to review selectors`);
      setImportOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
  };

  const saveFhir = async () => {
    const e = editingFhir;
    if (!e?.name?.trim() || !e?.fhir_base_url?.trim()) {
      toast.error('Name and FHIR base URL are required');
      return;
    }
    const payload = {
      name: e.name,
      vendor: e.vendor || 'custom',
      fhir_base_url: e.fhir_base_url,
      auth_type: e.auth_type || 'none',
      enabled: e.enabled !== false,
    };
    if (payload.auth_type === 'client_credentials') {
      payload.token_url = e.token_url || undefined;
      payload.client_id = e.client_id || undefined;
      if (e.client_secret) payload.client_secret = e.client_secret;
      payload.scope = e.scope || undefined;
    }
    if (payload.auth_type === 'bearer' && e.static_bearer_token) {
      payload.static_bearer_token = e.static_bearer_token;
    }
    try {
      if (e.id) {
        await axios.patch(`${API}/integrations/fhir/${e.id}`, payload, authHeader);
        toast.success('FHIR config updated');
      } else {
        await axios.post(`${API}/integrations/fhir`, payload, authHeader);
        toast.success('FHIR config added');
      }
      setEditingFhir(null);
      setShowSecret(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    }
  };

  const deleteFhir = async (id) => {
    if (!window.confirm('Delete this FHIR integration? Any cached tokens will be purged.')) return;
    try {
      await axios.delete(`${API}/integrations/fhir/${id}`, authHeader);
      toast.success('Deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete');
    }
  };

  const testFhir = async (cfg) => {
    const id = cfg.id;
    const key = `testing_${id}`;
    setFhirConfigs((list) => list.map((c) => (c.id === id ? { ...c, [key]: true } : c)));
    try {
      const { data } = await axios.post(`${API}/integrations/fhir/${id}/test`, {}, authHeader);
      toast.success(`Connected — ${data.software || 'FHIR server'} (${data.fhir_version || 'unknown version'})`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Connection failed', { duration: 6000 });
    } finally {
      setFhirConfigs((list) => list.map((c) => (c.id === id ? { ...c, [key]: false } : c)));
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#F9F6F0', fontFamily: "'Manrope', sans-serif" }} data-testid="integrations-page">
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900">
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </button>
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] px-2 py-1 rounded-full" style={{ background: '#E3E8E4', color: FOREST }}>
            Enterprise
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3" style={{ color: CHARCOAL }}>
            <Plug className="w-7 h-7" style={{ color: FOREST }} />
            Custom EHR Integrations
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Connect Plexura to your EHR without waiting for us — self-service DOM selectors for the browser extension, and direct FHIR API integration for backend push.
          </p>
        </div>

        {/* Tabs */}
        <div className="inline-flex rounded-full p-1 mb-6 border" style={{ background: '#fff', borderColor: '#E3E8E4' }}>
          <button
            onClick={() => setTab('selectors')}
            data-testid="tab-selectors"
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-2 ${tab === 'selectors' ? 'text-white' : 'text-stone-600 hover:text-stone-900'}`}
            style={tab === 'selectors' ? { background: FOREST } : {}}
          >
            <Code2 className="w-4 h-4" /> Custom Selectors
          </button>
          <button
            onClick={() => setTab('fhir')}
            data-testid="tab-fhir"
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-2 ${tab === 'fhir' ? 'text-white' : 'text-stone-600 hover:text-stone-900'}`}
            style={tab === 'fhir' ? { background: FOREST } : {}}
          >
            <Server className="w-4 h-4" /> FHIR APIs
          </button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-stone-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        )}

        {/* ── Custom selectors tab ─────────────────────── */}
        {!loading && tab === 'selectors' && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <p className="text-sm text-stone-500 max-w-2xl">
                Teach the Plexura browser extension how to inject notes into any EHR web page — just provide a URL pattern and the CSS selectors of each SOAP field. Works automatically across all clinicians in your practice after they refresh their extension.
              </p>
              <div className="flex gap-2 shrink-0">
                <Button
                  onClick={() => setImportOpen(true)}
                  data-testid="open-import-btn"
                  variant="outline"
                  className="h-10 rounded-xl font-semibold px-4 border-violet-700 text-violet-800 hover:bg-violet-50 hover:text-violet-900 hover:border-violet-900"
                >
                  <Upload className="w-4 h-4 mr-1" /> Import template
                </Button>
                <Button
                  onClick={() => setEditingSelector({ name: '', url_pattern: '', selectors: {}, enabled: true })}
                  data-testid="add-custom-ehr-btn"
                  className="h-10 rounded-xl text-white font-semibold px-4"
                  style={{ background: FOREST }}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add custom EHR
                </Button>
              </div>
            </div>

            {importOpen && (
              <ImportTemplateDialog
                authHeader={authHeader}
                onClose={() => setImportOpen(false)}
                onImport={importTemplate}
              />
            )}

            {customEhrs.length === 0 && !editingSelector && (
              <div className="text-sm text-stone-500 py-8 border border-dashed rounded-2xl text-center" style={{ borderColor: '#E3E8E4' }}>
                No custom selectors yet. Click <strong>Add custom EHR</strong> to create one.
              </div>
            )}

            {editingSelector && (
              <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm space-y-4" data-testid="selector-editor">
                <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: CHARCOAL }}>
                  <Globe className="w-5 h-5" style={{ color: TERRA }} />
                  {editingSelector.id ? 'Edit custom EHR' : 'New custom EHR'}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Friendly name</Label>
                    <Input
                      value={editingSelector.name}
                      onChange={(e) => setEditingSelector({ ...editingSelector, name: e.target.value })}
                      placeholder="Our In-House EHR"
                      data-testid="selector-name-input"
                      className="mt-1.5 h-11 rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">URL pattern</Label>
                    <Input
                      value={editingSelector.url_pattern}
                      onChange={(e) => setEditingSelector({ ...editingSelector, url_pattern: e.target.value })}
                      placeholder="*.myehr.com/* or myclinic.com/charts/*"
                      data-testid="selector-url-pattern-input"
                      className="mt-1.5 h-11 rounded-xl font-mono text-xs" />
                    <p className="text-[11px] text-stone-400 mt-1">Glob syntax — the extension matches this against the full page URL.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  {SELECTOR_FIELDS.map((f) => (
                    <div key={f.key}>
                      <Label className="text-xs font-medium text-stone-500">{f.label}</Label>
                      <Input
                        value={editingSelector.selectors?.[f.key] || ''}
                        onChange={(e) => setEditingSelector({
                          ...editingSelector,
                          selectors: { ...(editingSelector.selectors || {}), [f.key]: e.target.value },
                        })}
                        placeholder={f.placeholder}
                        data-testid={`selector-${f.key}-input`}
                        className="mt-1 h-9 rounded-lg font-mono text-xs" />
                    </div>
                  ))}
                </div>

                <div>
                  <Label className="text-xs font-medium text-stone-500">Notes page hints (comma-separated URL/title words)</Label>
                  <Input
                    value={(editingSelector.note_page_hints || []).join(', ')}
                    onChange={(e) => setEditingSelector({
                      ...editingSelector,
                      note_page_hints: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                    })}
                    placeholder="chart, note, encounter, visit"
                    className="mt-1 h-9 rounded-lg font-mono text-xs" />
                </div>

                {/* ── Note format template ──────────────────── */}
                <div className="pt-2 border-t border-stone-100">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-sm font-medium flex items-center gap-1.5" style={{ color: CHARCOAL }}>
                      <LayoutTemplate className="w-4 h-4" style={{ color: TERRA }} />
                      Note format template <span className="text-[11px] text-stone-400 font-normal">(optional — for single-textarea EHRs)</span>
                    </Label>
                    <TestRenderButton
                      noteFormat={editingSelector.note_format || ''}
                      authHeader={authHeader}
                    />
                  </div>
                  <p className="text-[11px] text-stone-500 mb-2">
                    When your EHR has a single notes textarea (the "Fallback" selector above), Plexura renders this template with the session's data and injects the result. Leave empty to push section-by-section using individual selectors.
                  </p>
                  <Input
                    value={editingSelector.note_format_title || ''}
                    onChange={(e) => setEditingSelector({ ...editingSelector, note_format_title: e.target.value })}
                    placeholder="Optional header shown in push preview (e.g. 'Progress Note')"
                    className="h-9 rounded-lg text-xs mb-2" />
                  <textarea
                    value={editingSelector.note_format || ''}
                    onChange={(e) => setEditingSelector({ ...editingSelector, note_format: e.target.value })}
                    data-testid="selector-note-format-input"
                    rows={10}
                    placeholder={`SUBJECTIVE\n{{subjective}}\n\nOBJECTIVE\n{{objective}}\n\nASSESSMENT\n{{assessment}}\n\nPLAN\n{{plan}}\n\nDIAGNOSIS: {{diagnosis_text}} ({{icd10_primary}})`}
                    className="w-full rounded-lg border px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2"
                    style={{ borderColor: '#E3E8E4', minHeight: 160 }}
                  />
                  <details className="mt-2">
                    <summary className="text-[11px] text-stone-500 cursor-pointer hover:text-stone-700">Available placeholders</summary>
                    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-stone-600">
                      {[
                        ['{{subjective}}', 'S section'],
                        ['{{objective}}', 'O section'],
                        ['{{assessment}}', 'A section'],
                        ['{{plan}}', 'P section'],
                        ['{{diagnosis_text}}', 'Primary Dx'],
                        ['{{icd10_primary}}', 'ICD-10 code'],
                        ['{{cpt_code}}', 'CPT'],
                        ['{{patient_name}}', 'Patient'],
                        ['{{session_date}}', 'Session date'],
                        ['{{session_duration}}', 'Duration'],
                        ['{{therapy_interventions}}', 'Interventions'],
                        ['{{provider_name}}', 'Clinician'],
                      ].map(([ph, hint]) => (
                        <div key={ph} className="flex items-center gap-2">
                          <code className="bg-stone-100 px-1.5 py-0.5 rounded">{ph}</code>
                          <span className="text-stone-400">{hint}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    id="enabled"
                    type="checkbox"
                    checked={editingSelector.enabled !== false}
                    onChange={(e) => setEditingSelector({ ...editingSelector, enabled: e.target.checked })}
                    className="w-4 h-4" />
                  <Label htmlFor="enabled" className="text-sm font-medium">Enabled — extension will use this on matching pages</Label>
                </div>

                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="outline" onClick={() => setEditingSelector(null)} className="rounded-xl">Cancel</Button>
                  <Button onClick={saveSelector} data-testid="selector-save-btn" className="rounded-xl text-white" style={{ background: FOREST }}>
                    Save custom EHR
                  </Button>
                </div>
              </div>
            )}

            {customEhrs.length > 0 && (
              <div className="divide-y divide-stone-100 mt-2">
                {customEhrs.map((c) => (
                  <div key={c.id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`custom-ehr-row-${c.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold" style={{ color: CHARCOAL }}>
                        {c.name}
                        {!c.enabled && <span className="ml-2 text-[10px] font-medium text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full">Disabled</span>}
                      </p>
                      <p className="text-xs text-stone-500 font-mono break-all">{c.url_pattern}</p>
                      <p className="text-[11px] text-stone-400 mt-0.5">
                        {Object.keys(c.selectors || {}).length} selector{Object.keys(c.selectors || {}).length === 1 ? '' : 's'} configured
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditingSelector(c)} className="rounded-lg" data-testid={`edit-selector-${c.id}`}>Edit</Button>
                      <Button variant="outline" size="sm" onClick={() => exportSelector(c)} className="rounded-lg" data-testid={`export-selector-${c.id}`}>
                        <Download className="w-3.5 h-3.5 mr-1" /> Export
                      </Button>
                      <button
                        onClick={() => deleteSelector(c.id)}
                        data-testid={`delete-selector-${c.id}`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 px-2.5 py-1.5 rounded-lg hover:bg-red-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── FHIR tab ─────────────────────────────────── */}
        {!loading && tab === 'fhir' && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <p className="text-sm text-stone-500 max-w-2xl">
                Connect directly to Epic, Cerner, athenahealth, MEDITECH, or any SMART-on-FHIR server. Plexura will authenticate via OAuth2 client credentials or a static bearer token, search patients, and push completed session notes as <code className="text-[11px] bg-stone-100 px-1 py-0.5 rounded">DocumentReference</code> resources.
              </p>
              <Button
                onClick={() => setEditingFhir({ name: '', vendor: 'custom', fhir_base_url: '', auth_type: 'none', enabled: true })}
                data-testid="add-fhir-btn"
                className="h-10 rounded-xl text-white font-semibold px-4 shrink-0"
                style={{ background: FOREST }}
              >
                <Plus className="w-4 h-4 mr-1" /> Add FHIR integration
              </Button>
            </div>

            {fhirConfigs.length === 0 && !editingFhir && (
              <div className="text-sm text-stone-500 py-8 border border-dashed rounded-2xl text-center" style={{ borderColor: '#E3E8E4' }}>
                No FHIR integrations yet. Click <strong>Add FHIR integration</strong> to connect one.
              </div>
            )}

            {editingFhir && (
              <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm space-y-4" data-testid="fhir-editor">
                <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: CHARCOAL }}>
                  <Server className="w-5 h-5" style={{ color: TERRA }} />
                  {editingFhir.id ? 'Edit FHIR integration' : 'New FHIR integration'}
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Friendly name</Label>
                    <Input
                      value={editingFhir.name}
                      onChange={(e) => setEditingFhir({ ...editingFhir, name: e.target.value })}
                      placeholder="Our Epic production"
                      data-testid="fhir-name-input"
                      className="mt-1.5 h-11 rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Vendor</Label>
                    <select
                      value={editingFhir.vendor || 'custom'}
                      onChange={(e) => setEditingFhir({ ...editingFhir, vendor: e.target.value })}
                      data-testid="fhir-vendor-select"
                      className="mt-1.5 w-full h-11 rounded-xl border px-3 text-sm bg-white"
                      style={{ borderColor: '#E3E8E4' }}
                    >
                      {VENDORS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium">FHIR base URL</Label>
                  <Input
                    value={editingFhir.fhir_base_url || ''}
                    onChange={(e) => setEditingFhir({ ...editingFhir, fhir_base_url: e.target.value })}
                    placeholder="https://fhir.epic.com/interconnect/api/FHIR/R4"
                    data-testid="fhir-base-url-input"
                    className="mt-1.5 h-11 rounded-xl font-mono text-xs" />
                </div>

                <div>
                  <Label className="text-sm font-medium">Authentication</Label>
                  <select
                    value={editingFhir.auth_type || 'none'}
                    onChange={(e) => setEditingFhir({ ...editingFhir, auth_type: e.target.value })}
                    data-testid="fhir-auth-type-select"
                    className="mt-1.5 w-full h-11 rounded-xl border px-3 text-sm bg-white"
                    style={{ borderColor: '#E3E8E4' }}
                  >
                    <option value="none">None (public sandbox)</option>
                    <option value="client_credentials">OAuth2 Client Credentials</option>
                    <option value="bearer">Static bearer token</option>
                  </select>
                </div>

                {editingFhir.auth_type === 'client_credentials' && (
                  <div className="space-y-3 bg-stone-50 border border-stone-100 rounded-xl p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-medium text-stone-500">Token URL</Label>
                        <Input
                          value={editingFhir.token_url || ''}
                          onChange={(e) => setEditingFhir({ ...editingFhir, token_url: e.target.value })}
                          placeholder="https://fhir.epic.com/.../token"
                          className="mt-1 h-9 rounded-lg font-mono text-xs" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-stone-500">Scope</Label>
                        <Input
                          value={editingFhir.scope || 'system/Patient.read system/DocumentReference.write'}
                          onChange={(e) => setEditingFhir({ ...editingFhir, scope: e.target.value })}
                          className="mt-1 h-9 rounded-lg font-mono text-xs" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-stone-500">Client ID</Label>
                        <Input
                          value={editingFhir.client_id || ''}
                          onChange={(e) => setEditingFhir({ ...editingFhir, client_id: e.target.value })}
                          className="mt-1 h-9 rounded-lg font-mono text-xs" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-stone-500">
                          Client Secret
                          {editingFhir.id && editingFhir.has_client_secret && !editingFhir.client_secret && (
                            <span className="ml-2 text-[10px] text-stone-400 font-normal">(leave blank to keep existing)</span>
                          )}
                        </Label>
                        <div className="mt-1 relative">
                          <Input
                            type={showSecret ? 'text' : 'password'}
                            value={editingFhir.client_secret || ''}
                            onChange={(e) => setEditingFhir({ ...editingFhir, client_secret: e.target.value })}
                            className="h-9 rounded-lg font-mono text-xs pr-9" />
                          <button
                            type="button"
                            onClick={() => setShowSecret(!showSecret)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                          >
                            {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {editingFhir.auth_type === 'bearer' && (
                  <div>
                    <Label className="text-xs font-medium text-stone-500">
                      Static bearer token
                      {editingFhir.id && editingFhir.has_client_secret && !editingFhir.static_bearer_token && (
                        <span className="ml-2 text-[10px] text-stone-400 font-normal">(leave blank to keep existing)</span>
                      )}
                    </Label>
                    <Input
                      type="password"
                      value={editingFhir.static_bearer_token || ''}
                      onChange={(e) => setEditingFhir({ ...editingFhir, static_bearer_token: e.target.value })}
                      className="mt-1 h-9 rounded-lg font-mono text-xs" />
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input
                    id="fhir_enabled"
                    type="checkbox"
                    checked={editingFhir.enabled !== false}
                    onChange={(e) => setEditingFhir({ ...editingFhir, enabled: e.target.checked })}
                    className="w-4 h-4" />
                  <Label htmlFor="fhir_enabled" className="text-sm font-medium">Enabled</Label>
                </div>

                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="outline" onClick={() => { setEditingFhir(null); setShowSecret(false); }} className="rounded-xl">Cancel</Button>
                  <Button onClick={saveFhir} data-testid="fhir-save-btn" className="rounded-xl text-white" style={{ background: FOREST }}>
                    Save FHIR config
                  </Button>
                </div>
              </div>
            )}

            {fhirConfigs.length > 0 && (
              <div className="divide-y divide-stone-100 mt-2">
                {fhirConfigs.map((c) => (
                  <div key={c.id} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`fhir-row-${c.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold flex items-center gap-2" style={{ color: CHARCOAL }}>
                        {c.name}
                        <span className="text-[10px] font-medium text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full uppercase tracking-wider">{c.vendor}</span>
                        {!c.enabled && <span className="text-[10px] font-medium text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full">Disabled</span>}
                        {c.last_test_status === 'ok' && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                            <CheckCircle2 className="w-3 h-3" /> Connected
                          </span>
                        )}
                        {c.last_test_status && c.last_test_status !== 'ok' && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
                            <XCircle className="w-3 h-3" /> {c.last_test_status}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-stone-500 font-mono break-all">{c.fhir_base_url}</p>
                      <p className="text-[11px] text-stone-400 mt-0.5">Auth: {c.auth_type}</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => testFhir(c)}
                        disabled={c.testing_}
                        data-testid={`test-fhir-${c.id}`}
                        className="rounded-lg"
                      >
                        {c[`testing_${c.id}`] ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plug className="w-3.5 h-3.5 mr-1" />}
                        Test
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setEditingFhir(c)} className="rounded-lg" data-testid={`edit-fhir-${c.id}`}>Edit</Button>
                      <FhirPatientSearchPopover cfg={c} authHeader={authHeader} />
                      <button
                        onClick={() => deleteFhir(c.id)}
                        data-testid={`delete-fhir-${c.id}`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 px-2.5 py-1.5 rounded-lg hover:bg-red-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function TestRenderButton({ noteFormat, authHeader }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const run = async () => {
    if (!noteFormat?.trim()) {
      toast.error('Enter a note format template first');
      return;
    }
    setLoading(true);
    setErr('');
    try {
      const { data } = await axios.post(
        `${API}/integrations/custom-ehrs/preview`,
        { note_format: noteFormat },
        authHeader,
      );
      setResult(data);
      setOpen(true);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Preview failed');
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(result?.rendered || ''); toast.success('Copied'); } catch {}
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={run}
        disabled={loading}
        data-testid="test-render-btn"
        className="rounded-lg text-xs"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
        Test render
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(28,25,23,0.55)', backdropFilter: 'blur(4px)' }}
          data-testid="test-render-dialog"
        >
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-5 border-b border-stone-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: CHARCOAL }}>
                <Wand2 className="w-5 h-5" style={{ color: TERRA }} />
                Test render preview
              </h2>
              <button onClick={() => { setOpen(false); setResult(null); setErr(''); }} className="text-stone-400 hover:text-stone-700 text-sm px-3 py-1 rounded-lg hover:bg-stone-50" data-testid="test-render-close">Close</button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              {err && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-red-100 bg-red-50 text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{err}</span>
                </div>
              )}

              {result && (
                <>
                  <p className="text-xs text-stone-500">
                    Rendered using the most recent completed session (<code className="bg-stone-100 px-1 rounded">{result.session_date || 'no date'}</code>). This is what will appear in the EHR when a clinician pushes a session using this template.
                  </p>

                  <div className="border rounded-xl bg-stone-50" style={{ borderColor: '#E3E8E4' }}>
                    <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: '#E3E8E4' }}>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">Rendered output</span>
                      <button onClick={copy} className="text-[11px] font-medium text-stone-600 hover:text-stone-900 inline-flex items-center gap-1 px-2 py-1 rounded-md hover:bg-stone-100" data-testid="test-render-copy">
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <pre
                      data-testid="test-render-output"
                      className="p-4 text-xs font-mono whitespace-pre-wrap break-words"
                      style={{ color: CHARCOAL, maxHeight: 360, overflow: 'auto' }}
                    >
{result.rendered}
                    </pre>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
                    <div className="p-2 rounded-lg bg-green-50 border border-green-100">
                      <p className="font-semibold text-green-800 mb-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Filled ({result.placeholders.filled.length})
                      </p>
                      <p className="font-mono text-green-700 break-all">
                        {result.placeholders.filled.length ? result.placeholders.filled.map(k => `{{${k}}}`).join(' ') : '—'}
                      </p>
                    </div>
                    <div className="p-2 rounded-lg bg-stone-50 border border-stone-100">
                      <p className="font-semibold text-stone-600 mb-1">Empty ({result.placeholders.empty.length})</p>
                      <p className="font-mono text-stone-500 break-all">
                        {result.placeholders.empty.length ? result.placeholders.empty.map(k => `{{${k}}}`).join(' ') : '—'}
                      </p>
                    </div>
                    <div className="p-2 rounded-lg bg-amber-50 border border-amber-100">
                      <p className="font-semibold text-amber-800 mb-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Unknown ({result.placeholders.unknown.length})
                      </p>
                      <p className="font-mono text-amber-700 break-all">
                        {result.placeholders.unknown.length ? result.placeholders.unknown.map(k => `{{${k}}}`).join(' ') : '—'}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ImportTemplateDialog({ authHeader, onClose, onImport }) {
  // Dialog-scoped accent — dark purple theme (requested). Overrides module-level FOREST
  // green just within this modal for visual distinction.
  const PURPLE = '#5B21B6';
  const PURPLE_DARK = '#4C1D95';
  const PURPLE_SOFT = '#EDE9FE';
  const PURPLE_BORDER = '#C4B5FD';
  const [mode, setMode] = useState('gallery'); // gallery | paste | upload | custom
  const [gallery, setGallery] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [flavors, setFlavors] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [gallerySearch, setGallerySearch] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [overrideName, setOverrideName] = useState('');
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState(null);
  const [parseError, setParseError] = useState('');

  // Custom-URL form
  const [customName, setCustomName] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [customFlavor, setCustomFlavor] = useState('soap');
  const [buildingCustom, setBuildingCustom] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/integrations/ehr-template-gallery`, authHeader);
        setGallery(data.presets || []);
        setCategories(data.categories || []);
        setFlavors(data.note_format_flavors || []);
        setFavorites(data.favorites || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Gallery unavailable');
      } finally {
        setLoadingGallery(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const tryParse = (text) => {
    setParseError('');
    setParsed(null);
    if (!text.trim()) return;
    try {
      const obj = JSON.parse(text);
      setParsed(obj);
    } catch (e) {
      setParseError('Not valid JSON — please check your paste.');
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const text = await file.text();
    setPasteText(text);
    tryParse(text);
  };

  const commitImport = () => {
    if (!parsed) {
      toast.error('Nothing to import');
      return;
    }
    onImport(parsed, overrideName);
  };

  const buildAndInstall = async () => {
    if (!customName.trim() || !customUrl.trim()) {
      toast.error('Enter both a name and a URL pattern');
      return;
    }
    setBuildingCustom(true);
    try {
      const { data } = await axios.post(
        `${API}/integrations/ehr-template-gallery/build`,
        { name: customName.trim(), url_pattern: customUrl.trim(), flavor: customFlavor },
        authHeader,
      );
      onImport(data.template);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not build template');
    } finally {
      setBuildingCustom(false);
    }
  };

  // Filter gallery by category + search string, favorites first
  const q = gallerySearch.trim().toLowerCase();
  const favSet = new Set(favorites);
  const filteredGallery = gallery
    .filter((p) => {
      if (activeCategory === 'favorites') return favSet.has(p.key);
      if (activeCategory !== 'all' && p.category !== activeCategory) return false;
      if (!q) return true;
      return (
        (p.label || '').toLowerCase().includes(q) ||
        (p.template?.url_pattern || '').toLowerCase().includes(q) ||
        (p.template?.note_format_title || '').toLowerCase().includes(q)
      );
    })
    .sort((a, b) => (favSet.has(b.key) ? 1 : 0) - (favSet.has(a.key) ? 1 : 0));

  const toggleFavorite = async (presetKey) => {
    try {
      if (favSet.has(presetKey)) {
        const { data } = await axios.delete(
          `${API}/integrations/ehr-template-gallery/favorites/${encodeURIComponent(presetKey)}`,
          authHeader,
        );
        setFavorites(data.favorites || []);
      } else {
        const { data } = await axios.post(
          `${API}/integrations/ehr-template-gallery/favorites`,
          { preset_key: presetKey },
          authHeader,
        );
        setFavorites(data.favorites || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update favorite');
    }
  };

  const categoryIcons = {
    sparkles: Sparkles, globe: Globe, heart: Heart,
    stethoscope: Stethoscope, building: Building2, star: Star,
  };
  const categoryCounts = gallery.reduce((acc, p) => {
    acc[p.category || 'primary_care'] = (acc[p.category || 'primary_care'] || 0) + 1;
    acc.all = (acc.all || 0) + 1;
    return acc;
  }, {});
  categoryCounts.favorites = favorites.length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(28,25,23,0.55)', backdropFilter: 'blur(4px)' }}
      data-testid="import-dialog"
    >
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-stone-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: CHARCOAL }}>
            <Upload className="w-5 h-5" style={{ color: PURPLE }} />
            Import EHR note template
          </h2>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700 text-sm px-3 py-1 rounded-lg hover:bg-stone-50" data-testid="import-dialog-close">Close</button>
        </div>

        <div className="p-5 flex gap-2 border-b border-stone-100 overflow-x-auto">
          {[
            { key: 'gallery', label: 'Gallery', icon: Sparkles },
            { key: 'custom', label: 'Custom URL', icon: Globe },
            { key: 'paste', label: 'Paste JSON', icon: ClipboardPaste },
            { key: 'upload', label: 'Upload file', icon: FileJson },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => { setMode(key); setParsed(null); setParseError(''); setPasteText(''); setFileName(''); }}
              data-testid={`import-mode-${key}`}
              className={`px-3 py-2 rounded-full text-xs font-semibold transition-colors inline-flex items-center gap-1.5 shrink-0 ${mode === key ? 'text-white' : 'text-stone-600 hover:text-stone-900 bg-stone-50'}`}
              style={mode === key ? { background: PURPLE } : {}}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {mode === 'gallery' && (
            <div className="space-y-3">
              <p className="text-sm text-stone-500">
                Pre-built templates for every EHR Plexura natively detects, in multiple note-format flavors. Install any one as a starting point — tweak selectors and format after import.
              </p>

              {/* Category chips */}
              <div className="flex flex-wrap gap-2" data-testid="gallery-category-chips">
                {categories.map((cat) => {
                  const Icon = categoryIcons[cat.icon] || Sparkles;
                  const count = categoryCounts[cat.key] || 0;
                  const active = activeCategory === cat.key;
                  return (
                    <button
                      key={cat.key}
                      onClick={() => setActiveCategory(cat.key)}
                      data-testid={`gallery-chip-${cat.key}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${active ? 'text-white shadow-sm' : 'text-stone-700 hover:text-stone-900 bg-white hover:bg-stone-50'}`}
                      style={active
                        ? { background: PURPLE, borderColor: PURPLE }
                        : { borderColor: PURPLE_BORDER }}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {cat.label}
                      <span className={`ml-1 text-[10px] font-bold ${active ? 'text-white/80' : 'text-stone-400'}`}>{count}</span>
                    </button>
                  );
                })}
              </div>

              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  value={gallerySearch}
                  onChange={(e) => setGallerySearch(e.target.value)}
                  placeholder="Search by EHR name, format, or URL…"
                  data-testid="gallery-search"
                  className="h-10 rounded-xl pl-9" />
              </div>
              {loadingGallery && <div className="text-sm text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
              <p className="text-[11px] text-stone-400 -mt-1">
                {filteredGallery.length} preset{filteredGallery.length === 1 ? '' : 's'}
                {activeCategory !== 'all' && ` in ${categories.find(c => c.key === activeCategory)?.label}`}
                {q && ` matching "${gallerySearch}"`}
                {!q && activeCategory === 'all' && ` · EHR not listed? Try the `}
                {!q && activeCategory === 'all' && <button onClick={() => setMode('custom')} className="underline text-stone-500 hover:text-stone-700">Custom URL tab</button>}
              </p>
              <div className="divide-y divide-stone-100 max-h-[50vh] overflow-auto -mr-2 pr-2">
                {filteredGallery.map((p) => {
                  const isFav = favSet.has(p.key);
                  return (
                    <div
                      key={p.key}
                      className="flex items-start justify-between gap-3 py-3"
                      data-testid={`gallery-item-${p.key}`}
                    >
                      <div className="min-w-0 flex items-start gap-2">
                        <button
                          type="button"
                          onClick={() => toggleFavorite(p.key)}
                          data-testid={`gallery-fav-${p.key}`}
                          title={isFav ? 'Remove from favorites' : 'Add to favorites'}
                          className="shrink-0 mt-0.5 p-1 rounded-md hover:bg-stone-100 transition-colors"
                        >
                          <Star
                            className="w-4 h-4"
                            style={{
                              color: isFav ? '#F5B400' : '#CBCBC1',
                              fill: isFav ? '#F5B400' : 'transparent',
                            }}
                          />
                        </button>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold" style={{ color: CHARCOAL }}>{p.label}</p>
                          <p className="text-xs text-stone-500 mt-0.5">{p.description}</p>
                          <p className="text-[11px] text-stone-400 font-mono mt-1">{p.template.url_pattern}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => onImport(p.template)}
                        data-testid={`gallery-install-${p.key}`}
                        className="rounded-lg text-white shrink-0"
                        style={{ background: PURPLE }}
                      >
                        Install
                      </Button>
                    </div>
                  );
                })}
                {!filteredGallery.length && !loadingGallery && (
                  <div className="text-sm text-stone-500 py-10 text-center" data-testid="gallery-empty-state">
                    {activeCategory === 'favorites'
                      ? <>No favorites yet. Click the <Star className="inline w-3.5 h-3.5 text-[#F5B400]" fill="#F5B400" /> next to any preset to add it here.</>
                      : <>No presets match "{gallerySearch}". <button onClick={() => setMode('custom')} className="underline font-medium" style={{ color: PURPLE_DARK }}>Create one for this EHR →</button></>
                    }
                  </div>
                )}
              </div>
            </div>
          )}

          {mode === 'custom' && (
            <div className="space-y-3">
              <p className="text-sm text-stone-500">
                Don't see your EHR in the gallery? Type its name and URL pattern below and pick a note format — Plexura will generate a starter template, install it, and you can fine-tune the CSS selectors after.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium">EHR name</Label>
                  <Input
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    placeholder="Our in-house chart tool"
                    data-testid="custom-url-name-input"
                    className="mt-1 h-11 rounded-xl" />
                </div>
                <div>
                  <Label className="text-sm font-medium">URL pattern</Label>
                  <Input
                    value={customUrl}
                    onChange={(e) => setCustomUrl(e.target.value)}
                    placeholder="*.myehr.com/* or charts.myclinic.com/*"
                    data-testid="custom-url-pattern-input"
                    className="mt-1 h-11 rounded-xl font-mono text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Note format flavor</Label>
                <select
                  value={customFlavor}
                  onChange={(e) => setCustomFlavor(e.target.value)}
                  data-testid="custom-url-flavor-select"
                  className="mt-1 w-full h-11 rounded-xl border px-3 text-sm bg-white"
                  style={{ borderColor: PURPLE_BORDER }}
                >
                  {flavors.map((f) => <option key={f.key} value={f.key}>{f.title}</option>)}
                </select>
                <p className="text-[11px] text-stone-400 mt-1">You can change or rewrite the format template after install.</p>
              </div>

              <div className="rounded-xl p-3 text-[11px] leading-relaxed" style={{ background: PURPLE_SOFT, border: `1px solid ${PURPLE_BORDER}`, color: PURPLE_DARK }}>
                <strong className="block mb-1" style={{ color: PURPLE_DARK }}>How it works</strong>
                Plexura will generate a custom EHR with a universal single-textarea selector (<code className="bg-white px-1 rounded">textarea, [contenteditable='true'], [role='textbox']</code>) and the format flavor you picked. When clinicians open any page matching your URL, the extension will offer a Push button. Click <strong>Edit</strong> afterwards to replace the selectors with CSS that targets your EHR's specific fields.
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
                <Button
                  onClick={buildAndInstall}
                  disabled={buildingCustom || !customName.trim() || !customUrl.trim()}
                  data-testid="custom-url-build-btn"
                  className="rounded-xl text-white"
                  style={{ background: PURPLE }}
                >
                  {buildingCustom ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  Generate & install
                </Button>
              </div>
            </div>
          )}

          {mode === 'paste' && (
            <div className="space-y-3">
              <p className="text-sm text-stone-500">
                Paste a template JSON bundle exported from Plexura (another practice can share theirs, or export from an existing custom EHR and adapt).
              </p>
              <textarea
                value={pasteText}
                onChange={(e) => { setPasteText(e.target.value); tryParse(e.target.value); }}
                rows={10}
                placeholder='{ "plexura_template": "custom_ehr", "name": "My EHR", "url_pattern": "*.example.com/*", "selectors": { ... }, "note_format": "..." }'
                data-testid="paste-json-input"
                className="w-full rounded-xl border px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2"
                style={{ borderColor: PURPLE_BORDER, minHeight: 200 }}
              />
              {parseError && <p className="text-xs text-red-600">{parseError}</p>}
              {parsed && (
                <div className="text-xs text-stone-500 bg-stone-50 border border-stone-100 rounded-lg p-3">
                  Preview: <strong>{parsed.name || '(no name)'}</strong> · <code>{parsed.url_pattern || '(no URL pattern)'}</code>
                </div>
              )}
            </div>
          )}

          {mode === 'upload' && (
            <div className="space-y-3">
              <p className="text-sm text-stone-500">
                Upload a <code>.json</code> file exported from another Plexura practice or your internal template library.
              </p>
              <label
                className="block border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors hover:bg-violet-50"
                style={{ borderColor: PURPLE_BORDER }}
                data-testid="upload-dropzone"
              >
                <FileJson className="w-8 h-8 mx-auto mb-2 text-stone-400" />
                <p className="text-sm font-medium" style={{ color: CHARCOAL }}>{fileName ? fileName : 'Click to select a .json template'}</p>
                <p className="text-[11px] text-stone-400 mt-1">or drop a file here</p>
                <input type="file" accept="application/json,.json" onChange={onFile} className="hidden" data-testid="upload-file-input" />
              </label>
              {parseError && <p className="text-xs text-red-600">{parseError}</p>}
              {parsed && (
                <div className="text-xs text-stone-500 bg-stone-50 border border-stone-100 rounded-lg p-3">
                  Preview: <strong>{parsed.name || '(no name)'}</strong> · <code>{parsed.url_pattern || '(no URL pattern)'}</code>
                </div>
              )}
            </div>
          )}

          {(mode === 'paste' || mode === 'upload') && parsed && (
            <div className="mt-4">
              <Label className="text-xs font-medium text-stone-500">Override name (optional)</Label>
              <Input
                value={overrideName}
                onChange={(e) => setOverrideName(e.target.value)}
                placeholder="Leave blank to keep the template's name"
                className="mt-1 h-9 rounded-lg text-xs" />
            </div>
          )}
        </div>

        {(mode === 'paste' || mode === 'upload') && (
          <div className="p-5 border-t border-stone-100 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
            <Button onClick={commitImport} disabled={!parsed} className="rounded-xl text-white" style={{ background: PURPLE }} data-testid="import-commit-btn">
              Import template
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function FhirPatientSearchPopover({ cfg, authHeader }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const search = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/integrations/fhir/${cfg.id}/patients?q=${encodeURIComponent(q)}`, authHeader);
      setResults(data.patients || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="rounded-lg">
        <Users className="w-3.5 h-3.5 mr-1" /> Patients
      </Button>
    );
  }
  return (
    <div className="w-full bg-white rounded-xl border border-stone-200 p-3 mt-1" data-testid={`fhir-patient-search-${cfg.id}`}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()}
            placeholder="Search patient by name…" className="h-9 rounded-lg pl-8" />
        </div>
        <Button size="sm" onClick={search} disabled={loading} className="rounded-lg text-white" style={{ background: FOREST }}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Search'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setOpen(false); setResults([]); setQ(''); }} className="rounded-lg">Close</Button>
      </div>
      {results.length > 0 && (
        <ul className="mt-2 divide-y divide-stone-100 max-h-64 overflow-auto">
          {results.map((p) => (
            <li key={p.id} className="py-2 text-sm flex justify-between items-center">
              <div>
                <p className="font-medium">{p.name || '(no name)'}</p>
                <p className="text-xs text-stone-500">ID: <code>{p.id}</code>{p.birthDate && ` · DOB ${p.birthDate}`}{p.gender && ` · ${p.gender}`}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
      {!loading && results.length === 0 && q && (
        <p className="mt-2 text-xs text-stone-500">No results. Try a different query.</p>
      )}
    </div>
  );
}
